import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useController } from 'react-hook-form';
import { PiTrashBold } from 'react-icons/pi';

import { cn } from '#/lib/utils';
import {
  Button,
  DESTRUCTIVE_GHOST_BUTTON_CLASS,
} from '#/modules/commonComponents/button';
import ListFieldMobileLabel from '#/modules/commonComponents/form/ListFieldMobileLabel';
import VoucherPickerDialog from '#/modules/finMgmt/claims/components/VoucherPickerDialog';
import { toVoucherSummary } from '#/modules/finMgmt/claims/data/voucherPickerColumns';
import { formatFmsAmount } from '#/modules/finMgmt/shared/helper/fmsAmount';
import { formatVoucherTypeLabel } from '#/modules/finMgmt/vouchers/helper/voucherStatus';
import { formatDateOrDash } from '#/utils/date';

/** 類型｜號碼｜日期｜總額｜移除（版型同請款明細 list） */
// eslint-disable-next-line max-len -- Tailwind arbitrary grid 須單一完整 class 字面量
const ROW_GRID_COLS = '@xl/form-section:grid-cols-[minmax(5rem,6rem)_minmax(10rem,1.6fr)_minmax(7rem,1fr)_minmax(7rem,1fr)_2.5rem]';

const HEADER_CLASS = cn(
  'hidden w-full gap-3 border-b border-gray-300 bg-gray-100 px-3 py-2',
  'text-xs font-medium text-gray-600',
  '@xl/form-section:grid',
  ROW_GRID_COLS,
);

const ROW_CLASS = cn(
  'grid w-full grid-cols-1 items-center gap-1 px-3 py-2',
  '@xl/form-section:gap-3',
  ROW_GRID_COLS,
);

const STACK_LABEL_CLASS = '@xl/form-section:hidden';

const Cell = ({ label, children, className }) => (
  <div className={cn('min-w-0 text-sm', className)}>
    <ListFieldMobileLabel className={STACK_LABEL_CLASS}>{label}</ListFieldMobileLabel>
    <span className="block truncate">{children}</span>
  </div>
);

Cell.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node,
  className: PropTypes.string,
};

/**
 * 請款單「引用憑證」：表單值仍是 voucherIds；顯示用摘要由 initialVouchers（編輯）與 dialog 選取累積。
 */
const PaymentRequestVouchersSection = ({
  control,
  errors,
  readOnly = false,
  initialVouchers = [],
}) => {
  const { field } = useController({ control, name: 'voucherIds' });
  const voucherIds = useMemo(() => field.value ?? [], [field.value]);
  const [pickedById, setPickedById] = useState({});
  const [pickerOpen, setPickerOpen] = useState(false);

  const summaryById = useMemo(() => {
    const map = {};
    (initialVouchers ?? []).forEach((item) => {
      if (item?.id) {
        map[String(item.id)] = toVoucherSummary(item);
      }
    });
    return { ...map, ...pickedById };
  }, [initialVouchers, pickedById]);

  const selectedRows = useMemo(
    () => voucherIds.map((id) => summaryById[String(id)] ?? { id: String(id) }),
    [voucherIds, summaryById],
  );

  const errorMessage = typeof errors?.voucherIds?.message === 'string'
    ? errors.voucherIds.message
    : null;

  function handleOpenPicker() {
    setPickerOpen(true);
  }

  function handlePickerConfirm(rows) {
    setPickedById((prev) => {
      const next = { ...prev };
      rows.forEach((row) => {
        next[row.id] = row;
      });
      return next;
    });
    field.onChange(rows.map((row) => row.id));
  }

  function handleRemove(id) {
    field.onChange(voucherIds.filter((item) => String(item) !== String(id)));
  }

  return (
    <section className="@container/form-section py-6">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h2 className="form-block__title mb-0!">引用憑證</h2>
        {!readOnly ? (
          <Button type="button" size="sm" onClick={handleOpenPicker}>
            選擇憑證
          </Button>
        ) : null}
      </div>

      {errorMessage ? (
        <p className="mb-3 text-sm text-red-500">{errorMessage}</p>
      ) : null}

      <div
        className={cn(
          'w-full overflow-hidden rounded-md border',
          errorMessage ? 'border-red-500' : 'border-gray-300',
        )}
      >
        <div className={HEADER_CLASS} aria-hidden>
          <span>類型</span>
          <span>憑證號碼</span>
          <span>憑證日期</span>
          <span className="text-right">總額</span>
          <span className="sr-only">操作</span>
        </div>
        {selectedRows.length === 0 ? (
          <p className="m-0 px-3 py-6 text-center text-sm text-muted-foreground">尚未引用憑證</p>
        ) : (
          <ul className="m-0 list-none divide-y divide-gray-200 p-0">
            {selectedRows.map((row) => (
              <li key={row.id} className={ROW_CLASS}>
                <Cell label="類型">{row.voucherType ? formatVoucherTypeLabel(row.voucherType) : '—'}</Cell>
                <Cell label="憑證號碼">{row.voucherNo || row.id}</Cell>
                <Cell label="憑證日期">{formatDateOrDash(row.voucherDate)}</Cell>
                <Cell label="總額" className="tabular-nums @xl/form-section:text-right">
                  {formatFmsAmount(row.totalAmount)}
                </Cell>
                <div className="flex min-w-0 @xl/form-section:justify-end">
                  {!readOnly ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      className={DESTRUCTIVE_GHOST_BUTTON_CLASS}
                      aria-label={`移除憑證 ${row.voucherNo || row.id}`}
                      onClick={() => handleRemove(row.id)}
                    >
                      <PiTrashBold className="size-4" />
                    </Button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* 每次開啟重掛，初始勾選取當下已選 */}
      {pickerOpen ? (
        <VoucherPickerDialog
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          initialSelectedRows={selectedRows}
          onConfirm={handlePickerConfirm}
        />
      ) : null}
    </section>
  );
};

PaymentRequestVouchersSection.propTypes = {
  control: PropTypes.object.isRequired,
  errors: PropTypes.object,
  readOnly: PropTypes.bool,
  initialVouchers: PropTypes.arrayOf(PropTypes.object),
};

export default PaymentRequestVouchersSection;
